import { FC, memo } from "react";

import {
  AlertTriangle,
  CheckCircle as IconCheck,
  CircleClear as IconCircle,
} from "@hl/base-components/lib/assets/icons.generated";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { ACCENT_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { maskAddress } from "@hl/communities-app/lib/utils/content";
import { EvmDeployMetadata } from "@hl/shared-features/lib/features/evm-tx";
import {
  TransactionStateType,
  useTransactionState,
} from "@hl/shared-features/lib/features/evm-tx/TransactionContext";
import {
  buildTxUrl,
  networkLookup,
} from "@hl/shared-features/lib/utils/blockExplorer";
import {
  Anchor,
  Button,
  Grid,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";

import { MINT_BASE_URL } from "config";

export interface CollectionDeployModalProps {
  txnId: string;
  open: boolean;
  manageCollectionHandler: () => void;
  onClose?: () => void;
  onRetryUpload: () => void;
  onRetryTransaction?: () => void;
}

const CollectionDeployModal: FC<CollectionDeployModalProps> = ({
  txnId,
  open,
  manageCollectionHandler,
  onClose,
  onRetryUpload,
  onRetryTransaction,
}) => {
  const theme = useMantineTheme();
  const transactionState = useTransactionState(txnId);

  if (!transactionState) {
    // this should bubble up to an error boundary and show an error page
    return <></>;
  }

  const args = transactionState.args;
  const txHash = transactionState.hash;
  const metadata = transactionState.metadata as EvmDeployMetadata;
  const network = args?.chainId ? networkLookup(args.chainId) : undefined;
  const etherscanUrl =
    txHash && network ? buildTxUrl(network.type, txHash) : undefined;
  const reducedAddress = maskAddress(txHash, 6, 4);

  const isUploadInProgress =
    transactionState.type === TransactionStateType.WaitingWeb2Update;
  const uploadError =
    transactionState.type === TransactionStateType.Web2UpdateError;
  const uploadingMetadata = (
    <Grid justify="space-between">
      <Group align="center" position={"apart"} spacing={10}>
        <Grid.Col span="content">
          {isUploadInProgress && (
            <Loader size={24} color={theme.colors.divider[0]} />
          )}
          {uploadError && (
            <AlertTriangle
              width={24}
              height={24}
              color={theme.fn.themeColor(theme.colors.errorStatus[0])}
            />
          )}
          {!isUploadInProgress && !uploadError && (
            <IconCheck
              height={24}
              width={24}
              color={theme.colors.affirmativeStatus[0]}
            />
          )}
        </Grid.Col>
      </Group>
      {!uploadError && (
        <Grid.Col span="auto">
          <Text size="sm">Uploading metadata to arweave</Text>
          <Text color={TEXT_COLOR.SECONDARY} size="xs">
            This may take a few minutes depending on file sizes
          </Text>
        </Grid.Col>
      )}
      {uploadError && (
        <>
          <Grid.Col span="auto">
            <Text size="sm">Upload failed</Text>
            <Text color={TEXT_COLOR.SECONDARY} size="xs">
              Retry the upload
            </Text>
          </Grid.Col>
          <Grid.Col span="content">
            <Button onClick={onRetryUpload}>Retry upload</Button>
          </Grid.Col>
        </>
      )}
    </Grid>
  );

  const canRetrySignTransaction =
    !!onRetryTransaction &&
    transactionState.type === TransactionStateType.SignTxRejected;
  const signTransactionInQueue = isUploadInProgress || uploadError;
  const signTransactionInProgress = [
    TransactionStateType.WaitingPrepareTx,
    TransactionStateType.WaitingSignedTx,
  ].includes(transactionState.type);
  const signTransactionError = [TransactionStateType.SignTxRejected].includes(
    transactionState.type
  );
  const signTransaction = (
    <Grid justify="space-between">
      <Group align="center" spacing={10}>
        <Grid.Col>
          {signTransactionInQueue && (
            <IconCircle
              height={24}
              width={24}
              color={theme.colors.divider[0]}
            />
          )}
          {signTransactionInProgress && (
            <Loader size={24} color={theme.colors.divider[0]} />
          )}
          {signTransactionError && (
            <AlertTriangle
              width={24}
              height={24}
              color={theme.fn.themeColor(theme.colors.errorStatus[0])}
            />
          )}
          {!signTransactionInQueue &&
            !signTransactionInProgress &&
            !signTransactionError && (
              <IconCheck
                height={24}
                width={24}
                color={theme.colors.affirmativeStatus[0]}
              />
            )}
        </Grid.Col>
      </Group>
      {!signTransactionError && (
        <Grid.Col span="auto">
          <Text size="sm">Sign transaction in your wallet</Text>
          <Text color={TEXT_COLOR.SECONDARY} size="xs">
            Confirm this transaction by signing in your wallet.
          </Text>
        </Grid.Col>
      )}
      {signTransactionError && (
        <>
          <Grid.Col span="auto">
            <Text size="sm">Sign transaction failed</Text>
            <Text color={TEXT_COLOR.SECONDARY} size="xs">
              Confirm this transaction by signing in your wallet.
            </Text>
          </Grid.Col>
          {canRetrySignTransaction && (
            <Grid.Col span="content">
              <Button onClick={onRetryTransaction}>Send Transaction</Button>
            </Grid.Col>
          )}
        </>
      )}
    </Grid>
  );

  const waitingConfirmationInQueue = !transactionState.hash;
  const waitingConfirmationInProgress =
    transactionState.type === TransactionStateType.WaitingConfirmedTx;
  const waitingConfirmationError =
    transactionState.type === TransactionStateType.ConfirmedTxError;
  const waitingConfirmation = (
    <Grid justify="space-between">
      <Group>
        <Grid.Col>
          {waitingConfirmationInQueue && (
            <IconCircle
              height={24}
              width={24}
              color={theme.colors.divider[0]}
            />
          )}
          {waitingConfirmationInProgress && (
            <Loader size={24} color={theme.colors.divider[0]} />
          )}
          {waitingConfirmationError && (
            <AlertTriangle
              width={24}
              height={24}
              color={theme.fn.themeColor(theme.colors.errorStatus[0])}
            />
          )}
          {!waitingConfirmationInQueue &&
            !waitingConfirmationInProgress &&
            !waitingConfirmationError && (
              <IconCheck
                height={24}
                width={24}
                color={theme.colors.affirmativeStatus[0]}
              />
            )}
        </Grid.Col>
      </Group>
      <Grid.Col span="auto">
        {waitingConfirmationInQueue && (
          <>
            <Text size="sm">
              Submit transaction{network ? ` to ${network.displayName}` : ""}
            </Text>
            <Text color={TEXT_COLOR.SECONDARY} size="xs">
              Contract deployment submitted
              {network ? ` to ${network.displayName}` : ""}
            </Text>
          </>
        )}
        {!waitingConfirmationInQueue && (
          <>
            <Text size="sm">Waiting for confirmation</Text>
            <Text color={TEXT_COLOR.SECONDARY} size="xs">
              Transaction submitted{network ? ` to ${network.displayName}` : ""}
              :
              <Anchor
                size="xs"
                href={etherscanUrl}
                color={ACCENT_COLOR.ACCENT}
                target="_blank"
                rel="noreferrer"
              >
                {reducedAddress}
              </Anchor>
            </Text>
          </>
        )}
      </Grid.Col>
    </Grid>
  );

  // if the transaction has been signed and there are no errors, the modal can
  // be hidden and process will complete in the background via CollectionDeployer
  const canBackground = !!transactionState.createdEvmTx;
  const mintUrl = metadata?.collectionId
    ? `${MINT_BASE_URL}/${metadata?.collectionId}`
    : "";
  return (
    <Modal
      size="lg"
      opened={open}
      title={
        <Text size="lg" weight={500}>
          Deploying{network ? ` on ${network.displayName}` : undefined}...
        </Text>
      }
      withCloseButton={canBackground}
      closeButtonLabel={"Hide"}
      onClose={() => onClose?.()}
    >
      <Stack spacing={24} mt={12}>
        {uploadingMetadata}
        {signTransaction}
        {waitingConfirmation}

        {canBackground && (
          <Grid>
            <Grid.Col span={6}>
              <Button
                size="xl"
                w="100%"
                color={SECONDARY_COLOR}
                component="a"
                href={mintUrl}
                target="_blank"
                disabled={!mintUrl}
              >
                View project page
              </Button>
            </Grid.Col>
            <Grid.Col span={6}>
              <Button size="xl" w="100%" onClick={manageCollectionHandler}>
                Manage collection
              </Button>
            </Grid.Col>
          </Grid>
        )}
      </Stack>
    </Modal>
  );
};

export default memo(CollectionDeployModal);
